import { useCallback, useEffect, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { ncfAPI } from '../../services/api';
import Typeahead from '../Typeahead';

const EMPTY_EDIT = {
  show: false,
  statusId: null,
  code: '',
  name: '',
  notificationEnabled: false,
  notificationMessageTemplate: '',
  notificationRecipientRoles: [],
  notificationAdditionalRecipients: []
};

const RECIPIENT_ROLES = [
  { key: 'submitter', label: 'Submitter' },
  { key: 'salesperson', label: 'Salesperson' },
  { key: 'recruiter', label: 'Recruiter' },
  { key: 'hr', label: 'NCF box' }
];

function NcfStatusAdmin() {
  const [statuses, setStatuses] = useState([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [draggedStatusId, setDraggedStatusId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [editModal, setEditModal] = useState(EMPTY_EDIT);

  const load = async () => {
    try { const response = await ncfAPI.adminStatuses(); setStatuses(response.data.statuses || []); }
    catch (err) { setError(err?.response?.data?.error || 'Could not load statuses.'); }
  };

  useEffect(() => { load(); }, []);

  const add = async (event) => {
    event.preventDefault(); setError(''); setMessage('');
    const nextSortOrder = Math.max(0, ...statuses.filter(status => status.isActive).map(status => Number(status.sortOrder) || 0)) + 10;
    try { await ncfAPI.createStatus({ code, name, sortOrder: nextSortOrder }); setCode(''); setName(''); await load(); setMessage('Status added.'); }
    catch (err) { setError(err?.response?.data?.error || 'Could not add status.'); }
  };

  const openEdit = (status) => setEditModal({
    show: true,
    statusId: status.statusId,
    code: status.code,
    name: status.name,
    notificationEnabled: Boolean(status.notificationEnabled),
    notificationMessageTemplate: status.notificationMessageTemplate || '',
    notificationRecipientRoles: status.notificationRecipientRoles || [],
    notificationAdditionalRecipients: status.notificationAdditionalRecipients || []
  });

  const setEditField = (name, value) => setEditModal(previous => ({ ...previous, [name]: value }));

  const toggleRecipientRole = (role) => setEditModal(previous => ({
    ...previous,
    notificationRecipientRoles: previous.notificationRecipientRoles.includes(role)
      ? previous.notificationRecipientRoles.filter(item => item !== role)
      : [...previous.notificationRecipientRoles, role]
  }));

  const searchDirectory = useCallback(async (query, { signal } = {}) => {
    const response = await ncfAPI.searchSubmissionDirectory(query, { signal });
    return (response.data.recipients || []).map(item => ({
      id: item.id,
      label: item.name || item.email,
      sublabel: `${item.email} • ${item.type === 'group' ? 'Group' : 'User'}`,
      raw: item
    }));
  }, []);

  const addRecipient = (option) => {
    if (!option?.raw) return;
    setEditModal(previous => previous.notificationAdditionalRecipients.some(item => item.email.toLowerCase() === option.raw.email.toLowerCase())
      ? previous
      : { ...previous, notificationAdditionalRecipients: [...previous.notificationAdditionalRecipients, option.raw] });
  };

  const saveEdit = async () => {
    setError(''); setMessage('');
    if (!editModal.name.trim()) { setError('Status name is required.'); return; }
    if (editModal.notificationEnabled && !editModal.notificationMessageTemplate.trim()) { setError('Enter a notification message.'); return; }
    if (editModal.notificationEnabled && editModal.notificationRecipientRoles.length === 0 && editModal.notificationAdditionalRecipients.length === 0) { setError('Select at least one notification recipient.'); return; }
    setBusy(true);
    try {
      await ncfAPI.updateStatusDefinition(editModal.statusId, {
        name: editModal.name.trim(),
        notificationEnabled: editModal.notificationEnabled,
        notificationMessageTemplate: editModal.notificationMessageTemplate,
        notificationRecipientRoles: editModal.notificationRecipientRoles,
        notificationAdditionalRecipients: editModal.notificationAdditionalRecipients
      });
      setEditModal(EMPTY_EDIT);
      await load();
      setMessage('Status workflow updated.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not update status.');
    } finally { setBusy(false); }
  };

  const toggle = async (status) => {
    const activeStatuses = statuses.filter(item => item.isActive);
    const restoreSortOrder = Math.max(0, ...activeStatuses.map(item => Number(item.sortOrder) || 0)) + 10;
    try { await ncfAPI.updateStatusDefinition(status.statusId, { isActive: !status.isActive, ...(!status.isActive ? { sortOrder: restoreSortOrder } : {}) }); await load(); setMessage(status.isActive ? 'Status removed from the picker.' : 'Status restored at the end of the picker.'); }
    catch (err) { setError(err?.response?.data?.error || 'Could not change status.'); }
  };

  const activeStatuses = statuses.filter(status => status.isActive);
  const inactiveStatuses = statuses.filter(status => !status.isActive);

  const persistStatusOrder = async (reordered) => {
    setReordering(true); setError(''); setMessage('');
    try {
      await Promise.all(reordered.map((status, orderIndex) => ncfAPI.updateStatusDefinition(status.statusId, { sortOrder: (orderIndex + 1) * 10 })));
      await load();
      setMessage('Status order updated.');
    } catch (err) {
      await load();
      setError(err?.response?.data?.error || 'Could not reorder statuses.');
    } finally { setReordering(false); }
  };

  const finishDragging = () => { setDraggedStatusId(null); setDropTarget(null); };

  const dropStatus = async (event, targetStatus) => {
    event.preventDefault();
    const draggedId = Number(draggedStatusId);
    if (!draggedId || draggedId === targetStatus.statusId || reordering) { finishDragging(); return; }
    const reordered = activeStatuses.filter(status => status.statusId !== draggedId);
    const targetIndex = reordered.findIndex(status => status.statusId === targetStatus.statusId);
    const insertIndex = targetIndex + (dropTarget?.position === 'after' ? 1 : 0);
    const draggedStatus = activeStatuses.find(status => status.statusId === draggedId);
    reordered.splice(insertIndex, 0, draggedStatus);
    finishDragging();
    await persistStatusOrder(reordered);
  };

  const renderStatusRow = (status) => <div
    className={`ncf-status-admin-row${status.isActive ? '' : ' is-inactive'}${draggedStatusId === status.statusId ? ' is-dragging' : ''}${dropTarget?.statusId === status.statusId ? ` is-drop-${dropTarget.position}` : ''}`}
    key={status.statusId}
    onDragOver={status.isActive ? (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      const bounds = event.currentTarget.getBoundingClientRect();
      setDropTarget({ statusId: status.statusId, position: event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after' });
    } : undefined}
    onDrop={status.isActive ? event => dropStatus(event, status) : undefined}
  >
    {status.isActive && <span
      className="ncf-status-drag-handle"
      draggable={!reordering}
      onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', String(status.statusId)); setDraggedStatusId(status.statusId); }}
      onDragEnd={finishDragging}
      title="Drag to reorder"
      aria-label={`Drag ${status.name} to reorder`}
    ><GripVertical size={20} /></span>}
    <span className="ncf-status-admin-copy"><strong>{status.name}</strong><small>{status.code}</small>{status.notificationEnabled && <small className="ncf-status-notification-summary">Notification on • {[...(status.notificationRecipientRoles || []).map(role => RECIPIENT_ROLES.find(item => item.key === role)?.label || role), ...(status.notificationAdditionalRecipients || []).map(item => item.name || item.email)].join(', ')}</small>}</span>
    <span className="ncf-status-admin-actions"><button className="btn btn-secondary" type="button" onClick={() => openEdit(status)}>Edit</button><button className={status.isActive ? 'btn btn-danger' : 'btn btn-secondary'} type="button" onClick={() => toggle(status)}>{status.isActive ? 'Remove' : 'Restore'}</button></span>
  </div>;

  return <section className="ncf-status-admin">
    <div className="ncf-section-header"><div><h2>Status options</h2><p>Control dropdown order, workflow statuses, and notifications.</p></div></div>
    {error && <div className="alert alert-error">{error}</div>}
    {message && <div className="alert alert-info">{message}</div>}
    <form className="ncf-status-add" onSubmit={add}><input value={code} onChange={event => setCode(event.target.value)} placeholder="Code, e.g. agreement-sent" required /><input value={name} onChange={event => setName(event.target.value)} placeholder="Display name" required /><button className="btn btn-primary" type="submit">Add status</button></form>
    <div className={`ncf-status-admin-list${reordering ? ' is-saving-order' : ''}`}>{activeStatuses.map(status => renderStatusRow(status))}</div>
    {inactiveStatuses.length > 0 && <details className="ncf-inactive-statuses">
      <summary>Removed statuses <span>{inactiveStatuses.length}</span></summary>
      <div className="ncf-status-admin-list">{inactiveStatuses.map(status => renderStatusRow(status))}</div>
    </details>}
    {editModal.show && <div className="modal-overlay" onClick={() => !busy && setEditModal(EMPTY_EDIT)}>
      <div className="modal-content ncf-modal ncf-status-modal" onClick={event => event.stopPropagation()}>
        <div className="modal-header"><div><h2>Edit status</h2><p>{editModal.code}</p></div><button type="button" className="close-btn" onClick={() => setEditModal(EMPTY_EDIT)} disabled={busy}>×</button></div>
        {error && <div className="alert alert-error">{error}</div>}
        <label className="ncf-status-modal-field"><span>Display name</span><input value={editModal.name} onChange={event => setEditField('name', event.target.value)} /></label>
        <section className={`ncf-status-workflow-settings${editModal.notificationEnabled ? ' is-enabled' : ''}`}>
          <label className="ncf-status-notification-toggle"><input type="checkbox" checked={editModal.notificationEnabled} onChange={event => setEditField('notificationEnabled', event.target.checked)} /><span><strong>Send a notification when this status is selected</strong><small>Adds the message as an NCF comment and emails the selected recipients.</small></span></label>
          {editModal.notificationEnabled && <>
            <label className="ncf-status-modal-field"><span>Comment and email message</span><textarea rows="5" value={editModal.notificationMessageTemplate} onChange={event => setEditField('notificationMessageTemplate', event.target.value)} placeholder="{{candidateName}} is now {{statusName}}. {{note}}" /></label>
            <p className="form-hint">Available placeholders: {'{{candidateName}}'}, {'{{statusName}}'}, {'{{previousStatusName}}'}, {'{{changedBy}}'}, {'{{note}}'}</p>
            <fieldset className="ncf-status-recipient-fieldset"><legend>Notify</legend><div className="ncf-status-role-options">{RECIPIENT_ROLES.map(role => <label key={role.key}><input type="checkbox" checked={editModal.notificationRecipientRoles.includes(role.key)} onChange={() => toggleRecipientRole(role.key)} /> {role.label}</label>)}</div></fieldset>
            <label className="ncf-status-modal-field"><span>Additional users or groups</span></label>
            <Typeahead value={null} onSelect={addRecipient} fetchOptions={searchDirectory} minChars={2} placeholder="Search users or groups..." />
            {editModal.notificationAdditionalRecipients.length > 0 && <div className="ncf-status-recipient-list">{editModal.notificationAdditionalRecipients.map(recipient => <div key={recipient.email}><span><strong>{recipient.name || recipient.email}</strong><small>{recipient.email} • {recipient.type === 'group' ? 'Group' : 'User'}</small></span><button type="button" className="btn btn-danger btn-sm" onClick={() => setEditField('notificationAdditionalRecipients', editModal.notificationAdditionalRecipients.filter(item => item.email !== recipient.email))}>Remove</button></div>)}</div>}
          </>}
        </section>
        <div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setEditModal(EMPTY_EDIT)} disabled={busy}>Cancel</button><button type="button" className="btn btn-primary" onClick={saveEdit} disabled={busy}>{busy ? 'Saving...' : 'Save status'}</button></div>
      </div>
    </div>}
  </section>;
}

export default NcfStatusAdmin;